/**
 * AI Design Engine — public facade.
 *
 * The one entry point the Admin Portal (and later the OpenAI adapter) uses:
 *
 *   const result = generateTemplate(designSpec);
 *   // result.commands   → ordered structured editor commands (never SVG/HTML)
 *   // result.validation → typed issues from the validation engine
 *   // result.quality    → 0–100 score + per-metric breakdown
 *
 * Everything else is re-exported for advanced/direct use, but consumers
 * should not need to orchestrate the sub-engines themselves.
 */

import { compose, normalizeSpec } from './compositionEngine';
import { validateComposition } from './templateValidationEngine';
import { scoreComposition100 } from './qualityScoringEngine';

/**
 * Run the full pipeline: compose → validate → score.
 * Throws on structurally invalid specs (unknown preset/pairing/harmony);
 * design-level problems come back as validation issues instead.
 */
export const generateTemplate = (designSpec) => {
  const composition = compose(designSpec);
  const validation = validateComposition(composition);
  const quality = scoreComposition100(composition);
  return {
    spec: composition.spec,
    commands: composition.commands,
    layout: composition.layout,
    tokens: composition.tokens,
    validation,
    quality,
  };
};

export { compose, normalizeSpec } from './compositionEngine';
export { validateComposition, SEVERITY } from './templateValidationEngine';
export { scoreComposition100 } from './qualityScoringEngine';
export {
  registerCommand, createCommand, listCommands, getCommandDefinition,
  CommandBuffer, BUILT_IN_COMMAND_NAMES, resetCommandIds,
} from './commandEngine';
export { LAYOUT_PRESETS, listLayoutPresets, resolveLayout } from './layoutEngine';
export { GRAMMAR_NODES, expandGrammar, validateSequence } from './designGrammarEngine';
export {
  registerComponent, buildComponent, listComponents, BUILT_IN_COMPONENT_IDS,
} from './componentEngine';
export {
  FONT_PAIRINGS, listFontPairings, resolveTypography, SCALE_RATIOS,
} from './typographyEngine';
export {
  generatePalette, validatePaletteContrast, contrastRatio, readableTextOn, HARMONIES,
} from './colorHarmonyEngine';
export { resolveSpacing, listDensities, DENSITIES } from './spacingEngine';
export { alignInZone, snapToGrid, alignmentConsistency } from './alignmentEngine';
export { planDecorations, listDecorationStyles, DECORATION_STYLES } from './decorationEngine';
